import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { SignalMark } from "../components/Logo";
import { Loader2, MailCheck, ArrowLeft, KeyRound, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function AuthShell({ children, testid }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 bg-[#F8FAFC] text-[#0B1F3A]" style={{ fontFamily: "'Inter', sans-serif" }} data-testid={testid}>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
        <div className="mb-8"><SignalMark size={44} /></div>
        {children}
      </motion.div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-[#0B1F3A]/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#1F6FEB] focus:ring-1 focus:ring-[#1F6FEB] transition-colors";
const btnCls = "w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1F6FEB] text-white py-3.5 text-sm font-semibold hover:bg-[#1a5fd0] transition-colors disabled:opacity-60";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@")) return setError("Enter your email address");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell testid="forgot-page">
      {sent ? (
        <div data-testid="forgot-success">
          <span className="h-12 w-12 rounded-full grid place-items-center mb-5" style={{ background: "rgba(32,201,151,0.15)" }}>
            <MailCheck className="h-6 w-6" style={{ color: "#0f9d74" }} strokeWidth={1.5} />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight">Check your inbox</h1>
          <p className="text-sm text-[#0B1F3A]/55 mt-3 leading-relaxed">
            If an account exists for <strong>{email}</strong>, we&rsquo;ve sent a link to choose a new password.
            The link works once and expires in 1 hour.
          </p>
          <Link to="/login" data-testid="forgot-back-login" className="mt-8 inline-flex items-center gap-1.5 text-sm text-[#1F6FEB] hover:underline">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to sign in
          </Link>
        </div>
      ) : (
        <>
          <h1 className="text-2xl font-semibold tracking-tight">Forgot your password?</h1>
          <p className="text-sm text-[#0B1F3A]/55 mt-2 mb-8">Enter your account email and we&rsquo;ll send you a reset link.</p>
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" className={inputCls} />
            {error && <p data-testid="forgot-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5">{error}</p>}
            <button data-testid="forgot-submit" type="submit" disabled={busy} className={btnCls}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <MailCheck className="h-4 w-4" strokeWidth={2} />}
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
          <Link to="/login" data-testid="forgot-back-login" className="mt-8 inline-flex items-center gap-1.5 text-sm text-[#0B1F3A]/55 hover:text-[#0B1F3A] transition-colors">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to sign in
          </Link>
        </>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters");
    if (pw !== pw2) return setError("Passwords don't match");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell testid="reset-page">
        <h1 className="text-2xl font-semibold tracking-tight">Invalid reset link</h1>
        <p className="text-sm text-[#0B1F3A]/55 mt-3">This link is missing its token. Request a new one below.</p>
        <Link to="/forgot-password" className="mt-8 inline-flex items-center gap-1.5 text-sm text-[#1F6FEB] hover:underline">Request a new link</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell testid="reset-page">
      {done ? (
        <div data-testid="reset-success">
          <span className="h-12 w-12 rounded-full grid place-items-center mb-5" style={{ background: "rgba(32,201,151,0.15)" }}>
            <Check className="h-6 w-6" style={{ color: "#0f9d74" }} strokeWidth={2} />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight">Password updated</h1>
          <p className="text-sm text-[#0B1F3A]/55 mt-3">You can sign in with your new password now.</p>
          <button data-testid="reset-go-login" onClick={() => navigate("/login")} className={`${btnCls} mt-8`}>Go to sign in</button>
        </div>
      ) : (
        <>
          <h1 className="text-2xl font-semibold tracking-tight">Choose a new password</h1>
          <p className="text-sm text-[#0B1F3A]/55 mt-2 mb-8">At least 8 characters. This link works once.</p>
          <form onSubmit={submit} className="space-y-4">
            <input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" autoComplete="new-password" className={inputCls} />
            <input data-testid="reset-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" autoComplete="new-password" className={inputCls} />
            {error && <p data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5">{error}</p>}
            <button data-testid="reset-submit" type="submit" disabled={busy} className={btnCls}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <KeyRound className="h-4 w-4" strokeWidth={2} />}
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
